import { GameServices } from "game/GameServices";
import { View } from "@westpierstudio/luma/mvc";
import { Tween } from "@westpierstudio/luma/createjs";
import { AssetSpec, TextAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { WinDisplayConfig } from "./WinlinesModel";
import { ReelsLayout } from "components/reels/ReelsModel";
import { Win, SymbolPos } from "servertypes/SharedTypes";
import { devLog, drawBox, drawRect, drawRoundedRect, graphicsToTexture } from "utils/Misc";
import { Signal } from "type-signals";
import { times } from "lodash";
import { WinBoxDev } from "./WinBoxDev";

class WinLineSignals {
    winLineComplete = new Signal();
}

const COLOUR = 0xFFFFFF;

interface WinlinesSpec extends AssetSpec {
    winBoxWidth: number,
    winBoxHeight: number,
    valueBoxWidth: number,
    valueBoxHeight: number,
    valueBoxRadius: number,
    valueBoxY: number,
    bonusCompleteWidth: number,
    bonusCompleteHeight: number,
    bonusCompleteRadius: number,
    bonusCompleteY: number,
    winBoxLineWidth: number,
    showValue: boolean,
    lineValue: TextAssetSpec,
    colors: WinlineColors
}

interface WinlineColors {
    bonus: string,
    list: string[]
}

interface BonusDisplayConfigs {
    extraFreeSpins: WinDisplayConfig,
    bonusComplete: WinDisplayConfig,
}

export class WinLines extends View {
    private spec!: WinlinesSpec;
    private winBoxes!: WinBoxDev[][];
    private reelsLayout!: ReelsLayout;
    private winDisplayConfig!: WinDisplayConfig;
    private haveSetup = false;
    private bonusConfigs: BonusDisplayConfigs;

    private readonly COLOUR_LIST: number[];
    private readonly COLOUR_BONUS;

    signals = new WinLineSignals();

    constructor(protected override readonly services: GameServices) {
        super(services);
        this.bonusConfigs = this.services.resources.getSetting(`winDisplayBonus`);
        this.spec = services.resources.getSpec(`winlines`);

        this.COLOUR_LIST = this.spec.colors.list.map((hexStr) => parseInt(hexStr, 16));

        this.COLOUR_BONUS = parseInt(this.spec.colors.bonus, 16);
    }

    setup(reelsLayout: ReelsLayout): void {

        if (!this.haveSetup) {

            const { factory, resources } = this.services;

            factory.applySpec(this, this.spec);

            this.setupWinBoxes(reelsLayout);
            this.scale = { x: reelsLayout.scale, y: reelsLayout.scale };
            this.reelsLayout = reelsLayout;
            this.haveSetup = true;
        }
    }

    setWinDisplayConfig(config: WinDisplayConfig): void {
        this.winDisplayConfig = config;
    }

    private showBoxesWithValues(win: Win) {
        let symbolIndex, reelIndex, winBox;

        let firstBox = true;

        for (const value of win.pattern) {

            reelIndex = value.column;
            symbolIndex = value.row;

            winBox = this.winBoxes[reelIndex][symbolIndex];
            winBox.visible = true;

            if (reelIndex === 0 && firstBox && win.winIndex >= 0) {
                winBox.showValueBox(this.spec.showValue);
                winBox.setValue(win.moneyWon);
                firstBox = false;

            } else {
                winBox.showValueBox(false);
            }
        }
    }

    winLineLoop(win: Win[]) {
        this.createWinlineLoop(win, this.winDisplayConfig);
    }

    private createWinlineLoop(wins: Win[], config: WinDisplayConfig) {
        const tween = Tween.get(this);
        for (const win of wins) {
            tween.call(() => {
                this.setWinBoxColors(win);
                this.showBoxesWithValues(win);
            })
                .wait(config.displayWinTime)
                .call(() => this.hideAllWinBoxes())
                .wait(config.timeBetweenWins);
        }
        tween.loop = -1;
    }

    showBonusWin(symbols: SymbolPos[], isBonusComplete = false, winValue = 0) {
        this.showScatterWin(symbols, this.bonusConfigs.bonusComplete, isBonusComplete, winValue);
    }

    private showScatterWin(symbols: SymbolPos[], config: WinDisplayConfig, isBonusComplete = false, winValue = 0): void {
        let winBox;
        let toggleBonus = false;

        Tween.get(this)
            .call(() => {
                let firstBox = true;
                for (const symbol of symbols) {

                    winBox = this.winBoxes[symbol.column][symbol.row];
                    winBox.visible = true;
                    if (firstBox && isBonusComplete) {
                        if (winValue) {
                            if (toggleBonus) {
                                winBox.setBonusComplete();
                            }
                            else {
                                winBox.showValueBox(true);
                                winBox.setValue(winValue);
                            }
                            toggleBonus = !toggleBonus;
                        }
                        else {
                            winBox.setBonusComplete();
                        }
                        winBox.setColor(this.COLOUR_BONUS);
                        firstBox = false;
                    } else winBox.showValueBox(false);
                    winBox.setColor(this.COLOUR_BONUS);
                }
            })
            .wait(config.displayWinTime)
            .call(() => {
                this.hideAllWinBoxes();
            })
            .wait(config.timeBetweenWins).loop = -1;
    }

    private setBoxesVisible(symbols: SymbolPos[], visible = true) {
        let winBox;
        for (const symbol of symbols) {
            winBox = this.winBoxes[symbol.column][symbol.row];
            winBox.visible = visible;
        }
    }

    hideAllWinBoxes(): void {
        for (const set of this.winBoxes) {
            for (const box of set) {

                if (box) {
                    box.visible = false;
                }
            }
        }
    }

    override reset(): void {

        if (this.haveSetup) {

            Tween.removeTweens(this);
            this.hideAllWinBoxes();
        }
    }

    private setupWinBoxes(reelsLayout: ReelsLayout): void {

        this.winBoxes = times(reelsLayout.reelCount, () => []);

        const { winBoxHeight, winBoxLineWidth, winBoxWidth, valueBoxHeight, valueBoxWidth, valueBoxRadius, valueBoxY, lineValue,
            bonusCompleteHeight, bonusCompleteRadius, bonusCompleteWidth, bonusCompleteY } = this.spec;

        const boxGraphic = drawBox(winBoxWidth, winBoxHeight, winBoxLineWidth, COLOUR);
        const valueBoxGraphic = drawRoundedRect(valueBoxWidth + valueBoxRadius, valueBoxHeight, COLOUR, valueBoxRadius);
        const bonusCompleteBoxGraphic = drawRoundedRect(bonusCompleteWidth + bonusCompleteRadius, bonusCompleteHeight, COLOUR, bonusCompleteRadius);

        const textures = {
            box: graphicsToTexture(this.services, boxGraphic, this.services.layout.getTextureScale()),
            valueBox: graphicsToTexture(this.services, valueBoxGraphic, this.services.layout.getTextureScale()),
            bonusCompleteBox: graphicsToTexture(this.services, bonusCompleteBoxGraphic, this.services.layout.getTextureScale()),
        };

        const { positions, reelCount, symbolCount, symbolSize } = reelsLayout;

        for (let i = 0; i < reelCount; i++) {
            for (let j = 0; j < symbolCount; j++) {

                this.winBoxes[i][j] = new WinBoxDev(
                    this.services,
                    positions[i].x,
                    positions[i].y + (j + 1) * symbolSize.height,//hidden symbol
                    textures,
                    valueBoxY,
                    bonusCompleteY,
                    lineValue,
                    { width: valueBoxWidth, height: valueBoxHeight },
                    { width: bonusCompleteWidth, height: bonusCompleteHeight },
                );

                this.addChild(this.winBoxes[i][j]);
            }
        }
    }

    private setWinBoxColors(win: Win) {
        for (const value of win.pattern) {
            const winBox = this.winBoxes[value.column][value.row];
            if (win.winIndex >= 0) {
                winBox.setColor(this.COLOUR_LIST[win.winIndex]);
            }
            else {
                winBox.setColor(this.COLOUR_BONUS);
            }
        }
    }
}